import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkle, FilmSlate, MusicNotes, Broadcast, DownloadSimple, MagicWand, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { uploadMedia } from "../lib/uploadMedia";
import { renderAiVideo, decodeClip, sceneTimeline } from "../lib/aiVideoRenderer";
import AIVideoSceneCard from "../components/AIVideoSceneCard";
import MusicPicker from "../components/MusicPicker";
import PremiumGate from "../components/PremiumGate";

const DEFAULT_VOICE = "EXAVITQu4vr4xnSDxMaL";

/** AI Text-to-Video — brief → AI scene plan → images + narration → music → render → publish with "AI-generated" label. */
export default function AIVideoStudio() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(DEFAULT_VOICE);
  const [topic, setTopic] = useState("");
  const [style, setStyle] = useState("cinematic");
  const [nScenes, setNScenes] = useState(4);
  const [plan, setPlan] = useState(null);
  const [narr, setNarr] = useState({}); // index -> AudioBuffer
  const [busy, setBusy] = useState(""); // plan | assets | render | publish
  const [sceneBusy, setSceneBusy] = useState({});
  const [progress, setProgress] = useState("");
  const [music, setMusic] = useState(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [rendered, setRendered] = useState(null);
  const [caption, setCaption] = useState("");
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/ai/video/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/ai/voice/voices").then((r) => setVoices(r.data.voices.filter((v) => !v.is_premium || r.data.is_premium_user))).catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const makePlan = async () => {
    if (topic.trim().length < 4) return toast.error("Describe your video idea or paste a script");
    setBusy("plan");
    try {
      const { data } = await api.post("/ai/video/plan", { topic, style, scenes: nScenes });
      setPlan(data); setNarr({}); setRendered(null);
      setCaption([data.caption, ...(data.hashtags || []).map((h) => `#${h}`)].filter(Boolean).join(" ").slice(0, 220));
      const { data: cat } = await api.get("/music/catalog", { params: { mood: data.music_mood, limit: 5 } });
      setMusic(cat.items?.[0] || null);
      setMeta((m) => m && ({ ...m, usage: { videos_today: m.usage.videos_today + 1 } }));
      toast.success(`Planned ${data.scenes.length} scenes — now generate the visuals`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Planning failed"); }
    finally { setBusy(""); }
  };

  const editScene = (i, patch) => setPlan((p) => ({ ...p, scenes: p.scenes.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const saveScene = async (i) => {
    const s = plan.scenes[i];
    await api.put(`/ai/video/plans/${plan.id}/scene`, { index: i, narration: s.narration, visual_prompt: s.visual_prompt, caption: s.caption }).catch(() => {});
  };

  const genImage = async (i) => {
    setSceneBusy((b) => ({ ...b, [i]: "image" }));
    try {
      await saveScene(i);
      const { data } = await api.post(`/ai/video/plans/${plan.id}/scene-image`, { index: i });
      editScene(i, { image_url: data.image_url, image_path: data.image_path });
    } catch (e) { toast.error(e?.response?.data?.detail || `Scene ${i + 1} image failed`); }
    finally { setSceneBusy((b) => ({ ...b, [i]: "" })); }
  };

  const genVoice = async (i) => {
    const s = plan.scenes[i];
    if (!s.narration?.trim()) return;
    setSceneBusy((b) => ({ ...b, [i]: "voice" }));
    try {
      const { data } = await api.post("/ai/voice/tts", { text: s.narration, voice_id: voiceId, kind: "video", title: `${plan.title} · scene ${i + 1}` });
      await api.put(`/ai/video/plans/${plan.id}/scene`, { index: i, audio_id: data.id }).catch(() => {});
      const buf = await decodeClip(data.url);
      setNarr((n) => ({ ...n, [i]: buf }));
      editScene(i, { audio_id: data.id, audio_url: data.url });
    } catch (e) { toast.error(e?.response?.data?.detail || `Scene ${i + 1} narration failed`); }
    finally { setSceneBusy((b) => ({ ...b, [i]: "" })); }
  };

  const genAll = async () => {
    setBusy("assets"); setRendered(null);
    try {
      for (let i = 0; i < plan.scenes.length; i++) {
        setProgress(`Scene ${i + 1}/${plan.scenes.length} — artwork`);
        if (!plan.scenes[i].image_url) await genImage(i);
        setProgress(`Scene ${i + 1}/${plan.scenes.length} — narration`);
        if (!narr[i]) await genVoice(i);
      }
      toast.success("All assets ready — render your video");
    } finally { setBusy(""); setProgress(""); }
  };

  const playScene = (i) => {
    if (playing === i) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const a = new Audio(fileUrl(plan.scenes[i].audio_url));
    a.onended = () => setPlaying(null);
    a.play().catch(() => {});
    audioRef.current = a; setPlaying(i);
  };

  const render = async () => {
    setBusy("render"); setProgress("Rendering…");
    try {
      const narrations = plan.scenes.map((_, i) => narr[i] || null);
      const musicUrl = music ? fileUrl(`/api/music/${music.id}/stream`) : null;
      const { blob, total } = await renderAiVideo({
        scenes: plan.scenes, narrations, musicUrl, title: plan.title,
        onProgress: ({ percent }) => setProgress(`Rendering ${Math.round(percent || 0)}%`),
      });
      if (rendered?.url) URL.revokeObjectURL(rendered.url);
      setRendered({ blob, url: URL.createObjectURL(blob), total });
      toast.success(`Rendered ${Math.round(total)}s video — preview below`);
    } catch (e) { toast.error(e?.message || "Render failed"); }
    finally { setBusy(""); setProgress(""); }
  };

  const publish = async () => {
    if (!rendered) return;
    setBusy("publish");
    try {
      const file = new File([rendered.blob], `ai-video-${Date.now()}.webm`, { type: "video/webm" });
      const up = await uploadMedia(file, { onProgress: (p) => setProgress(`Uploading ${p}%`) });
      const body = { caption, media_path: up.path, media_type: up.media_type, privacy: "public", music_id: music?.id || null, ai_labels: ["ai_video", ...(narrationCount ? ["ai_voice"] : [])] };
      if (up.mux) { body.mux_upload_id = up.mux.upload_id; body.mux_passthrough = up.mux.passthrough; body.mux_status = up.mux.status; }
      const { data: post } = await api.post("/posts", body);
      await api.post(`/ai/video/plans/${plan.id}/published`, { post_id: post.id }).catch(() => {});
      toast.success("Published with the AI-generated label ✦");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(""); setProgress(""); }
  };

  const download = () => { const a = document.createElement("a"); a.href = rendered.url; a.download = `${plan.title || "ai-video"}.webm`; a.click(); };

  const narrationCount = plan ? plan.scenes.filter((_, i) => narr[i]).length : 0;
  const imageCount = plan ? plan.scenes.filter((s) => s.image_url).length : 0;
  const allReady = plan && imageCount === plan.scenes.length && narrationCount === plan.scenes.length;
  const est = plan ? sceneTimeline(plan.scenes, plan.scenes.map((_, i) => narr[i] || null)).total : 0;
  const left = meta ? Math.max(0, meta.limits.videos_per_day - meta.usage.videos_today) : 0;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="ai-video-studio">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="aivideo-back"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio · AI</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Text to Video</h1>
        </div>
        {meta && <span className="text-cream/50 text-[11px]" data-testid="aivideo-usage">{left} of {meta.limits.videos_per_day} left today</span>}
      </div>

      {meta && <div className="mt-5"><PremiumGate used={meta.usage.videos_today} limit={meta.limits.videos_per_day} unit="videos today" isPremium={meta.is_premium} testid="aivideo-gate" /></div>}

      {/* Step 1 — brief */}
      <section className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-4">
        <p className="text-cream/70 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">1 · Your idea or script</p>
        <textarea value={topic} onChange={(e) => setTopic(e.target.value.slice(0, 1200))} rows={3} disabled={!!plan} data-testid="aivideo-topic" placeholder="e.g. 3 quick tips to learn a dancehall routine faster, hype and funny" className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm disabled:opacity-60" />
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="aivideo-styles">
          {(meta?.styles || []).map((s) => (
            <button key={s.id} disabled={!!plan} onClick={() => setStyle(s.id)} data-testid={`aivideo-style-${s.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${style === s.id ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"} disabled:opacity-60`}>{s.label}</button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Scenes</label>
          {[2, 3, 4, 5, 6].map((n) => <button key={n} disabled={!!plan} onClick={() => setNScenes(n)} data-testid={`aivideo-scenes-${n}`} className={`w-9 h-9 rounded-full text-xs font-black ${nScenes === n ? "bg-papaya text-ink" : "bg-white/5 text-cream/70 border border-white/10"} disabled:opacity-60`}>{n}</button>)}
          <div className="flex-1" />
          <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} data-testid="aivideo-voice" className="bg-white/5 border border-white/10 rounded-full px-3 py-2 text-cream text-xs focus:outline-none">
            {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{`🎙 ${v.name} · ${v.accent}`}</option>)}
          </select>
        </div>
        {!plan ? (
          <button onClick={makePlan} disabled={busy !== "" || left <= 0 || !meta?.enabled} data-testid="aivideo-plan" className="mt-4 w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            <MagicWand size={18} weight="fill" /> {busy === "plan" ? "Planning with AI…" : "Plan my video"}
          </button>
        ) : (
          <button onClick={() => { setPlan(null); setNarr({}); setRendered(null); }} data-testid="aivideo-reset" className="mt-3 text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-1"><X size={12} /> Start over</button>
        )}
      </section>

      {/* Step 2 — scenes */}
      {plan && (
        <section className="mt-6" data-testid="aivideo-plan-result">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-cream/70 text-[10px] tracking-[0.25em] uppercase font-bold">2 · Scenes</p>
              <h2 className="text-cream font-black text-lg" data-testid="aivideo-title">{plan.title}</h2>
            </div>
            <span className="text-cream/50 text-[11px]">{imageCount}/{plan.scenes.length} images · {narrationCount}/{plan.scenes.length} voices</span>
          </div>
          <div className="space-y-3">
            {plan.scenes.map((s, i) => (
              <AIVideoSceneCard key={i} index={i} scene={s} narration={narr[i]} busy={sceneBusy[i]} playing={playing === i}
                onEdit={(patch) => { editScene(i, patch); if (patch.narration !== undefined) setNarr((n) => { const c = { ...n }; delete c[i]; return c; }); }}
                onImage={() => genImage(i)} onNarration={() => genVoice(i)} onPlay={() => playScene(i)} />
            ))}
          </div>
          {!allReady && (
            <button onClick={genAll} disabled={busy !== ""} data-testid="aivideo-generate-assets" className="mt-4 w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
              <Sparkle size={18} weight="fill" /> {busy === "assets" ? progress || "Generating…" : "Generate visuals & narration"}
            </button>
          )}
        </section>
      )}

      {/* Step 3 — music + render */}
      {plan && (
        <section className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-4">
          <p className="text-cream/70 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">3 · Music & render</p>
          <button type="button" onClick={() => setMusicOpen(true)} data-testid="aivideo-music" className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border bg-white/5 border-white/10 hover:border-papaya/40 text-left">
            <div className="w-9 h-9 rounded-xl bg-papaya/20 flex items-center justify-center text-lg">{music ? music.cover_emoji || "🎵" : <MusicNotes size={16} className="text-papaya" weight="fill" />}</div>
            <div className="flex-1 min-w-0">
              <p className="text-cream font-bold text-sm truncate">{music ? music.title : "No music"}</p>
              <p className="text-cream/50 text-[11px] truncate">{music ? `${music.artist} · ${music.mood}` : "Tap to add a track"}</p>
            </div>
            {music && <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setMusic(null); }} onKeyDown={() => {}} className="text-cream/50"><X size={16} /></span>}
          </button>
          <button onClick={render} disabled={busy !== "" || imageCount === 0} data-testid="aivideo-render" className="mt-3 w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            <FilmSlate size={18} weight="fill" /> {busy === "render" ? progress || "Rendering…" : `Render video · ~${Math.round(est)}s`}
          </button>
          {!allReady && imageCount > 0 && <p className="text-cream/40 text-[11px] mt-2">Scenes without artwork use a gradient; scenes without narration stay silent.</p>}
        </section>
      )}

      {/* Step 4 — preview + publish */}
      {rendered && (
        <section className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="aivideo-result">
          <p className="text-cream/70 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">4 · Preview & publish</p>
          <div className="rounded-2xl overflow-hidden bg-black relative max-w-xs mx-auto">
            <video src={rendered.url} controls playsInline className="w-full aspect-[9/16] object-contain" data-testid="aivideo-preview" />
            <span className="absolute top-3 left-3 crystal-glass text-cream text-[9px] font-black tracking-[0.2em] uppercase px-2 py-1 rounded-full">✦ AI-generated</span>
          </div>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 220))} rows={2} data-testid="aivideo-caption" className="mt-3 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream text-sm focus:outline-none focus:border-papaya" />
          <p className="text-cream/40 text-[11px] mt-2">Posts made here carry an “AI generated” label so viewers know.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={download} data-testid="aivideo-download" className="flex-1 py-3 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5"><DownloadSimple size={14} weight="bold" /> Download</button>
            <button onClick={publish} disabled={busy !== ""} data-testid="aivideo-publish" className="flex-1 py-3 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><Broadcast size={14} weight="fill" /> {busy === "publish" ? progress || "Publishing…" : "Publish"}</button>
          </div>
        </section>
      )}

      {musicOpen && <MusicPicker selectedId={music?.id} onClose={() => setMusicOpen(false)} onSelect={(m) => { setMusic(m); setMusicOpen(false); }} />}
    </div>
  );
}
